// libraries.
import { useEffect, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { useController, useFormContext } from 'react-hook-form';

// styles.
import { WrapperInputField } from '@shared/InputField/styles';
import { ConfidenceLevel } from '@domains/documents-review/hooks/useFieldConfidence';
import { withRequiredMark } from '@shared/utils/fieldLabel';

interface MultiSelectDropdownFieldProps {
  label: string;
  name: string;
  options: { value: string; label: string }[];
  required?: boolean;
  confidenceLevel?: ConfidenceLevel;
  confidenceBorderColor?: string;
  confidenceBoxShadow?: string;
  onFieldEdit?: (fieldName: string) => void;
}

export const MultiSelectDropdownField = ({
  label,
  name,
  options,
  required = false,
  confidenceLevel,
  confidenceBorderColor,
  confidenceBoxShadow,
  onFieldEdit,
}: MultiSelectDropdownFieldProps) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: required ? { required: 'Campo requerido' } : {},
    defaultValue: [],
  });

  const [selected, setSelected] = useState<string[]>(value || []);

  const handleSelect = (optionValue: string) => {
    const newSelected = selected.includes(optionValue)
      ? selected.filter(item => item !== optionValue)
      : [...selected, optionValue];

    setSelected(newSelected);
    onChange(newSelected.join(';'));

    // Call the onFieldEdit callback when user edits
    if (onFieldEdit) {
      onFieldEdit(name);
    }
  };

  const isNoneSelected = selected.includes('none');

  const confidenceStyle =
    confidenceLevel && confidenceBorderColor && confidenceBoxShadow
      ? {
          border: `1px solid ${confidenceBorderColor}`,
          boxShadow: confidenceBoxShadow,
        }
      : {};

  const isDisabledByConfidence = confidenceLevel === 'HIGH';

  useEffect(() => {
    let formValueArray: string[] = [];

    if (Array.isArray(value)) {
      formValueArray = value;
    } else if (typeof value === 'string') {
      formValueArray = value.split(/[,;]/).filter(Boolean);
    }
    setSelected(formValueArray);

    if (formValueArray.length > 0) {
      onChange(formValueArray.join(';'));
    }
  }, [value]);

  return (
    <WrapperInputField>
      <label className="form-label" htmlFor={name}>
        {withRequiredMark(label, required)}
      </label>
      <Dropdown>
        <Dropdown.Toggle
          className="w-100 multi-dropdown"
          style={confidenceStyle}
        >
          {selected.length > 0
            ? `${selected.length} opción(es) seleccionada(s)`
            : 'Seleccionar opciones'}
        </Dropdown.Toggle>
        <Dropdown.Menu className="wrapper-items" flip={true}>
          {options.map(option => {
            const isOptionNone = option.value === 'none';
            const isDisabled =
              (!isOptionNone && isNoneSelected) || isDisabledByConfidence;
            return (
              <Dropdown.Item
                key={option.value}
                as="div"
                onClick={e => e.stopPropagation()}
              >
                <Form.Check
                  type="checkbox"
                  id={`checkbox-${option.value}`}
                  label={option.label}
                  disabled={isDisabled}
                  checked={selected.some(sel => sel === option.value)}
                  onChange={e => {
                    handleSelect(option.value);
                  }}
                />
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
      <div className="d-flex justify-content-center">
        {error && (
          <small className="text-danger my-1 fw-bold">{error.message}</small>
        )}
      </div>
    </WrapperInputField>
  );
};
